import { Injectable, inject } from '@angular/core';
import { Platform, AlertController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { environment } from '@env/environment';

/**
 * Remote app configuration served as a static JSON file from the media CDN
 * (`${mediaBaseUrl}/app-config.json`). Editing that file lets us raise the
 * minimum supported version without republishing the app or deploying backend.
 */
interface RemoteAppConfig {
  /** Minimum supported app version as semver, e.g. "1.4.0". */
  minSupportedVersion?: string;
  /** iOS App Store numeric app id (for `itms-apps://`/`apps.apple.com`). */
  appleAppId?: string;
  /** Full iOS App Store URL; takes precedence over `appleAppId` when set. */
  appStoreUrl?: string;
}

/**
 * MinVersionGuardService — hard version gate, independent of Google Play.
 *
 * On startup it compares the installed app version (from `@capacitor/app`)
 * against a minimum version. The minimum is read from a remote JSON file so it
 * can be changed server-side; if that fetch fails it falls back to the value
 * baked into `environment.minSupportedVersion`.
 *
 * When the installed version is below the minimum, it shows a non-dismissible
 * blocking alert whose only action opens the store. This is the safety net for
 * cases where the Play in-app update flow is unavailable or was bypassed.
 *
 * Only enforces on Android native — no-ops on web/iOS.
 */
@Injectable({ providedIn: 'root' })
export class MinVersionGuardService {
  private readonly platform = inject(Platform);
  private readonly alertCtrl = inject(AlertController);

  private static readonly REMOTE_CONFIG_TIMEOUT_MS = 5000;

  /** Remote config from the last successful fetch, reused for store URL resolution. */
  private lastRemoteConfig: RemoteAppConfig | null = null;

  /**
   * Enforce the minimum supported version. Returns `true` when the app is
   * blocked (version too low), `false` otherwise. Safe on any platform.
   */
  async enforce(): Promise<boolean> {
    // Enforce on native mobile (Android AND iOS), never on web/mobileweb.
    // Android behavior is preserved unchanged; iOS is newly enabled (Req 5.2).
    const isNativeMobile =
      (this.platform.is('android') || this.platform.is('ios')) &&
      !this.platform.is('mobileweb');
    if (!isNativeMobile) {
      return false;
    }

    let currentVersion: string;
    try {
      // Dynamic import to avoid a hard dependency on @capacitor/app in web preview.
      const { App } = await import('@capacitor/app');
      const info = await App.getInfo();
      currentVersion = info.version;
    } catch (error) {
      // Cannot determine the version (e.g. web fallback) — don't block.
      console.warn('[MinVersionGuard] Could not read app version:', error);
      return false;
    }

    const minVersion = await this.resolveMinVersion();

    if (this.isOutdated(currentVersion, minVersion)) {
      console.warn(
        `[MinVersionGuard] App ${currentVersion} is below minimum ${minVersion} — blocking.`,
      );
      await this.showBlockingAlert();
      return true;
    }

    return false;
  }

  /**
   * Resolve the minimum version: prefer the remote config, fall back to the
   * value compiled into the environment.
   */
  private async resolveMinVersion(): Promise<string> {
    const fallback = environment.minSupportedVersion;
    const base = environment.mediaBaseUrl;
    if (!base) {
      return fallback;
    }

    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(),
      MinVersionGuardService.REMOTE_CONFIG_TIMEOUT_MS,
    );

    try {
      const res = await fetch(`${base}/app-config.json`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) {
        return fallback;
      }
      const config = (await res.json()) as RemoteAppConfig;
      this.lastRemoteConfig = config; // reused by resolveAppStoreUrl (Req 5.5)
      const remote = config.minSupportedVersion;
      return typeof remote === 'string' && this.isValidSemver(remote) ? remote : fallback;
    } catch (error) {
      console.warn('[MinVersionGuard] Remote app-config fetch failed, using fallback:', error);
      return fallback;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Compare two semver strings. Returns `true` when `current` is strictly
   * lower than `min`. Missing or non-numeric parts are treated as 0.
   */
  private isOutdated(current: string, min: string): boolean {
    const c = this.parseVersion(current);
    const m = this.parseVersion(min);
    for (let i = 0; i < 3; i++) {
      if (c[i] < m[i]) return true;
      if (c[i] > m[i]) return false;
    }
    return false;
  }

  /** Parse "1.2.3" (ignoring any pre-release/build suffix) into [1, 2, 3]. */
  private parseVersion(version: string): [number, number, number] {
    const core = version.trim().split(/[-+]/)[0];
    const parts = core.split('.').map((p) => {
      const n = Number.parseInt(p, 10);
      return Number.isFinite(n) ? n : 0;
    });
    return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
  }

  private isValidSemver(version: string): boolean {
    return /^\d+\.\d+\.\d+/.test(version.trim());
  }

  /**
   * Non-dismissible blocking alert. The only action opens the store listing;
   * the alert re-presents itself so the technician cannot get past it.
   *
   * Store destination is resolved per platform (Req 5.3, 5.4):
   * - Android: `AppUpdate.openAppStore()` (Google Play) — unchanged.
   * - iOS: `App.openUrl()` to the App Store listing, since
   *   `@capawesome/capacitor-app-update` targets Google Play.
   * The message is store-neutral (no longer says "Google Play").
   */
  private async showBlockingAlert(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Actualización Obligatoria',
      message:
        'Tu versión de la app ya no es compatible. Debes actualizar desde la tienda de aplicaciones para poder continuar.',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Actualizar',
          handler: () => {
            void this.openStore();
            return false; // keep the alert open — no way past it
          },
        },
      ],
    });
    await alert.present();
  }

  /** Open the platform's store listing. */
  private async openStore(): Promise<void> {
    if (Capacitor.getPlatform() === 'ios') {
      const url = this.resolveAppStoreUrl();
      if (url) {
        try {
          const { App } = await import('@capacitor/app');
          await App.openUrl({ url });
          return;
        } catch (error) {
          console.warn('[MinVersionGuard] Could not open App Store URL:', error);
        }
      }
      // No appleAppId/appStoreUrl configured yet — fail safe (alert stays open).
      return;
    }
    // Android (and any other native target that supports it): Google Play.
    void AppUpdate.openAppStore();
  }

  /**
   * Build the iOS App Store URL from the environment. Prefers a full
   * `appStoreUrl`; otherwise builds it from the numeric `appleAppId`.
   * Returns `undefined` when neither is configured (still pending App Store
   * Connect app creation — P6).
   */
  private resolveAppStoreUrl(): string | undefined {
    const env = environment as { appStoreUrl?: string; appleAppId?: string };
    const remote = this.lastRemoteConfig;

    // Precedence: remote full URL → remote id → env full URL → env id.
    if (remote?.appStoreUrl) return remote.appStoreUrl;
    if (remote?.appleAppId) return `https://apps.apple.com/app/id${remote.appleAppId}`;
    if (env.appStoreUrl) return env.appStoreUrl;
    if (env.appleAppId) return `https://apps.apple.com/app/id${env.appleAppId}`;
    return undefined;
  }
}
